import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { analiseApi } from '@/lib/api/analise';
import { alertasApi, notificacoesApi } from '@/lib/api/core';
import { acoesApi } from '@/lib/api/acoes';
import { agendaApi } from '@/lib/api/agenda';
import { solicitacoesApi } from '@/lib/api/solicitacoes';
import { useBairros } from '@/lib/hooks/useBairros';
import { Info } from '@/design-system/Info';
import { useAuthStore } from '@/lib/stores/authStore';

const brl = (v: number) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const HORA = 60 * 60 * 1000;
const DIA = 24 * HORA;

/**
 * JANELA DO RADAR: o que já venceu, mais o que vence nas próximas 24 horas.
 *
 * O protótipo chama o bloco de "Radar do Dia" e monta a lista com o que ele
 * chama de "hoje" — mas "hoje" num painel aberto às 23h esconderia o
 * compromisso de amanhã às 8h, que é exatamente o que ainda dá para preparar.
 * A janela de 24 horas é dita na tela, com essas palavras.
 */
const JANELA_RADAR = 24 * HORA;

type TomRadar = 'crit' | 'warn' | 'neutral';

interface ItemRadar {
  chave: string;
  titulo: string;
  detalhe: string;
  /** Instante do prazo (ms). É a ÚNICA chave de ordenação — ver o bloco abaixo. */
  prazo: number;
  /** Texto do prazo, em unidade real (dias/horas), nunca normalizado em 0-100. */
  selo: string;
  tom: TomRadar;
  destino: string;
  /** Por que o link não abre o registro específico. */
  tituloLink: string;
}

const ehMesmoDia = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function seloPrazo(prazo: number, agora: number): { selo: string; tom: TomRadar } {
  if (prazo < agora) {
    const dias = Math.floor((agora - prazo) / DIA);
    if (dias <= 0) return { selo: 'prazo vencido hoje', tom: 'crit' };
    return { selo: `${dias} ${dias === 1 ? 'dia' : 'dias'} de atraso`, tom: 'crit' };
  }
  const d = new Date(prazo);
  const quando = ehMesmoDia(d, new Date(agora)) ? 'hoje' : 'amanhã';
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return { selo: `${quando} às ${hora}`, tom: 'warn' };
}

export function DashPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const usuario = useAuthStore((s) => s.user);

  const { data: resumo, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analiseApi.dashboard(token),
    enabled: !!token,
  });

  const { data: alertas = [] } = useQuery({
    queryKey: ['alertas'],
    queryFn: () => alertasApi.list(token),
    enabled: !!token,
  });

  const { data: solicitacoes = [] } = useQuery({
    queryKey: ['solicitacoes', 'radar'],
    queryFn: () => solicitacoesApi.list({}, token),
    enabled: !!token,
  });

  const { data: eventos = [] } = useQuery({
    queryKey: ['agenda-eventos'],
    queryFn: () => agendaApi.list(token),
    enabled: !!token,
  });

  const { data: acoes = [] } = useQuery({
    queryKey: ['acoes', 'dash'],
    queryFn: () => acoesApi.list({}, token),
    enabled: !!token,
  });

  /**
   * O briefing do cabeçalho conta notificações não lidas (proto:854). O
   * servidor já sabe filtrar, então pedimos só as não lidas em vez de trazer
   * a caixa inteira para contar no navegador.
   */
  const { data: naoLidasLista = [] } = useQuery({
    queryKey: ['notificacoes', 'nao-lidas'],
    queryFn: () => notificacoesApi.list(token, true),
    enabled: !!token,
  });
  const naoLidas = naoLidasLista.length;

  /**
   * "Sexta, 21 de agosto" no protótipo é literal, porque o HTML é congelado.
   * Aqui é a data de hoje, com a inicial maiúscula que o pt-BR não dá sozinho.
   */
  const dataDeHoje = useMemo(() => {
    const texto = new Date().toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }, []);

  const { bairros } = useBairros();

  // Estado do drill-down. Vazio nos dois campos = nível Brasil.
  const [geo, setGeo] = useState<{ uf: string; municipio: string }>({ uf: '', municipio: '' });

  const criticos = alertas.filter((a) => a.severidade === 'critica');

  /**
   * RADAR DO DIA — o que foi possível calcular com lastro, e o que não foi.
   *
   * O protótipo (proto:834-840) ordena este bloco por um score composto e
   * anuncia a fórmula na própria tela: "urgência 35% + impacto 25% +
   * proximidade temporal 15% + importância do relacionamento 25%".
   *
   * Dos quatro componentes, UM tem lastro: `impacto` é
   * `terr.bairro.necessidade`, um índice 0-100 real. Os outros três são
   * CONSTANTES LITERAIS digitadas no JavaScript do protótipo —
   * `urg = s.sla < 0 ? 100 : 85`, `prox = 100`, `rel = lid ? 100 : optin ? 60 : 30`
   * (proto:835-839). Não saem de nenhum campo, nenhuma política e nenhuma
   * configuração; existem para dar um ranking plausível a dados de mentira.
   *
   * O componente sem saída é "importância do relacionamento", 25% do total:
   * `gab.contato` não guarda nenhuma medida de vínculo, e o único número de
   * vínculo do sistema — `gab.lideranca.saude` — é DIGITADO pelo gabinete,
   * existe só para lideranças e está documentado na própria entidade como
   * "não é score calculado". Dois dos três tipos de item deste radar
   * (compromisso de agenda e prazo de solicitação sem liderança vinculada)
   * não têm nem a quem atribuir esse número. Criar uma coluna nova não
   * resolveria: ela nasceria nula para todo mundo, e um quarto do score
   * continuaria sendo chute.
   *
   * Então o radar não exibe score. Ordena pelo PRAZO, que é dado real, em
   * unidade real (dias de atraso, hora do compromisso) — e a lacuna do score
   * está declarada na tela, logo abaixo da lista.
   */
  const agora = Date.now();
  const necessidadePorTerritorio = useMemo(() => {
    const m = new Map<string, number>();
    // A chave é o NOME do território: é o que os módulos gravam em
    // `solicitacao.bairro` e `acao.bairro`, e é por ele que
    // `bairros.service.ts` e `analise.service.ts` casam as contagens no
    // servidor. O `id` do `BairroView` é o slug e não casaria nada.
    for (const b of bairros) m.set(b.n.toLowerCase(), b.nec);
    return m;
  }, [bairros]);

  const radar = useMemo<ItemRadar[]>(() => {
    const limite = agora + JANELA_RADAR;
    const itens: ItemRadar[] = [];

    for (const s of solicitacoes) {
      if (s.status === 'concluida' || !s.sla_prazo) continue;
      const prazo = new Date(s.sla_prazo).getTime();
      if (Number.isNaN(prazo) || prazo > limite) continue;
      const { selo, tom } = seloPrazo(prazo, agora);
      const nec = s.bairro ? necessidadePorTerritorio.get(s.bairro.toLowerCase()) : undefined;
      itens.push({
        chave: `solic-${s.id}`,
        titulo: `${s.protocolo} · ${s.tema}`,
        detalhe: [
          s.bairro || 'sem território informado',
          nec === undefined ? null : `necessidade do território ${nec}/100`,
          `prioridade ${s.prioridade}`,
        ]
          .filter(Boolean)
          .join(' · '),
        prazo,
        selo,
        tom,
        destino: prazo < agora ? '/solicitacoes?recorte=sla_vencido' : '/solicitacoes?recorte=abertas',
        // Não existe `?solicitacao=<id>` na tela de Solicitações: os únicos
        // deep-links dela são `?recorte=` e `?bairro=`. Inventar um segundo
        // padrão de deep-link é justamente o que a Task 5.2 evitou.
        tituloLink: 'Abre a lista de solicitações no recorte correspondente',
      });
    }

    for (const ev of eventos) {
      const inicio = new Date(ev.inicio).getTime();
      if (Number.isNaN(inicio) || inicio > limite) continue;
      // Compromisso que já terminou não cobra preparo de ninguém.
      const fim = new Date(ev.fim).getTime();
      if (!Number.isNaN(fim) && fim < agora) continue;
      const { selo, tom } = seloPrazo(inicio, agora);
      const nec = ev.bairro ? necessidadePorTerritorio.get(ev.bairro.toLowerCase()) : undefined;
      itens.push({
        chave: `ev-${ev.id}`,
        titulo: ev.titulo,
        detalhe: [
          ev.publico ? 'compromisso público' : 'compromisso interno',
          ev.bairro || null,
          nec === undefined ? null : `necessidade do território ${nec}/100`,
        ]
          .filter(Boolean)
          .join(' · '),
        prazo: inicio,
        selo,
        tom: tom === 'crit' ? 'crit' : 'neutral',
        destino: '/agenda',
        tituloLink: 'Abre a agenda',
      });
    }

    return itens.sort((a, b) => a.prazo - b.prazo).slice(0, 5);
  }, [solicitacoes, eventos, necessidadePorTerritorio, agora]);

  /**
   * MAPA DE OPORTUNIDADES — fatos, sem "indicador de presença".
   *
   * O protótipo ordena por `nec*.7 + (100 - presença)*.3`, e a presença é
   * `visitas*12 + lideranças*14 + contatos/8 + ações*6` com teto em 100
   * (proto:832). Os seis multiplicadores são arbitrários: o próprio protótipo
   * os imprime no rodapé do bloco de visitas (proto:890) porque não há de onde
   * derivá-los. Aqui eles não aparecem, nem o número que sai deles.
   *
   * O que entra na lista são LACUNAS FACTUAIS, sem limiar inventado:
   * "nenhuma liderança ativa" e "nenhuma ação concluída" são zero contado, não
   * corte arbitrário — ao contrário do `pres<40` e do `ct<60` do protótipo.
   * A ordenação é por `necessidade`, o índice 0-100 real do território — que,
   * aliás, é o que o próprio rodapé do protótipo já dizia ("ordenados por
   * necessidade"), embora seu código ordenasse pelo score.
   */
  const oportunidades = useMemo(() => {
    const concluidasPorTerritorio = new Map<string, number>();
    for (const a of acoes) {
      if (a.status !== 'concluida' || !a.bairro) continue;
      const k = a.bairro.toLowerCase();
      concluidasPorTerritorio.set(k, (concluidasPorTerritorio.get(k) ?? 0) + 1);
    }

    return bairros
      .map((b) => {
        const concluidas = concluidasPorTerritorio.get(b.n.toLowerCase()) ?? 0;
        const motivos: string[] = [];
        if (b.liderancasAtivas === 0) motivos.push('sem liderança ativa');
        if (concluidas === 0) motivos.push('nenhuma ação concluída');
        return { b, concluidas, motivos };
      })
      .filter((x) => x.motivos.length > 0)
      .sort((x, y) => y.b.nec - x.b.nec)
      .slice(0, 5);
  }, [bairros, acoes]);

  /**
   * DRILL-DOWN Brasil › UF › município › território (proto:1330-1337).
   *
   * As UFs e os municípios saem dos territórios CADASTRADOS, nunca de uma
   * lista própria — é a mesma decisão já tomada nos selects em cascata de
   * Solicitações (`SolicitacoesPage.tsx:130-137`): uma constante de 27 UFs
   * ofereceria a federação inteira a um mandato que atua em uma.
   *
   * Por isso também não há mapa do Brasil: o protótipo desenha uma grade de
   * quadrados com coordenadas cravadas para as 27 unidades (`UF_GRID`,
   * proto:1331), que é justamente o tipo de constante nova que este sistema
   * não quer. O desenho só aparece onde existe malha real no banco
   * (`terr.bairro.mapa_path`), no nível de território.
   */
  const estados = useMemo(() => {
    const m = new Map<string, { uf: string; contatos: number; territorios: number }>();
    for (const b of bairros) {
      if (!b.uf) continue;
      const atual = m.get(b.uf) ?? { uf: b.uf, contatos: 0, territorios: 0 };
      atual.contatos += b.contatosCadastrados;
      atual.territorios += 1;
      m.set(b.uf, atual);
    }
    return [...m.values()].sort((a, b) => a.uf.localeCompare(b.uf));
  }, [bairros]);

  const municipios = useMemo(() => {
    const m = new Map<string, { municipio: string; contatos: number; territorios: number }>();
    for (const b of bairros) {
      if (!b.municipio || b.uf !== geo.uf) continue;
      const atual = m.get(b.municipio) ?? { municipio: b.municipio, contatos: 0, territorios: 0 };
      atual.contatos += b.contatosCadastrados;
      atual.territorios += 1;
      m.set(b.municipio, atual);
    }
    return [...m.values()].sort((a, b) => b.contatos - a.contatos);
  }, [bairros, geo.uf]);

  const territorios = useMemo(
    () =>
      bairros
        .filter((b) => b.uf === geo.uf && b.municipio === geo.municipio)
        .sort((a, b) => b.contatosCadastrados - a.contatosCadastrados),
    [bairros, geo.uf, geo.municipio],
  );

  const comMalha = territorios.filter((b) => b.path);
  const maxContatos = Math.max(1, ...territorios.map((b) => b.contatosCadastrados));
  const contatosNoCadastro = bairros.reduce((t, b) => t + b.contatosCadastrados, 0);

  if (isLoading || !resumo) {
    return (
      <div className="page" id="p-dashboard">
        <div className="empty">Carregando o panorama do mandato...</div>
      </div>
    );
  }

  /**
   * SERVIDOR DE VERSÃO ANTERIOR.
   *
   * Front e API são dois projetos publicados separadamente: durante alguns
   * minutos de cada implantação, um dos dois é mais novo que o outro. Um
   * resumo sem os campos do cabeçalho derrubaria esta tela inteira — a de
   * entrada do sistema — com tela branca e nenhuma explicação.
   *
   * Então a tela para aqui e diz o que houve. Preencher com zero seria pior:
   * "0 aniversariantes hoje" é uma afirmação sobre o gabinete, e o servidor
   * não disse isso — ele não disse nada.
   */
  const camposAusentes = (
    [
      ['aniversariantes', resumo.aniversariantes],
      ['lideranças', resumo.liderancas],
      ['compromissos de hoje', resumo.agenda?.compromissos_hoje],
      ['cadastros recentes', resumo.cadastros_recentes],
      ['solicitações por território', resumo.solicitacoes_por_territorio],
    ] as const
  )
    .filter(([, valor]) => valor === undefined || valor === null)
    .map(([nome]) => nome);

  if (camposAusentes.length > 0) {
    return (
      <div className="page" id="p-dashboard">
        <div className="head">
          <h1>Bom dia, {usuario?.nome ? usuario.nome.split(' ')[0] : 'gestor'}.</h1>
        </div>
        <div className="note warn" data-testid="resumo-servidor-antigo">
          <b>O servidor ainda não envia tudo o que esta tela mostra.</b> Faltam:{' '}
          {camposAusentes.join(', ')}. Isso acontece quando o site é publicado antes da API — os
          dois sobem separados. Nenhum dado foi perdido; recarregue em alguns instantes. Os números
          não aparecem zerados de propósito: zero seria uma afirmação sobre o gabinete que o
          servidor não fez.
        </div>
      </div>
    );
  }

  const taxaConclusao =
    resumo.solicitacoes.total > 0
      ? Math.round((resumo.solicitacoes.concluidas / resumo.solicitacoes.total) * 100)
      : 0;

  const execucao =
    resumo.emendas.indicado > 0
      ? Math.round((resumo.emendas.pago / resumo.emendas.indicado) * 100)
      : 0;

  return (
    <div className="page" id="p-dashboard">
      <div className="head">
        <div>
          <h1>Bom dia, {usuario?.nome ? usuario.nome.split(' ')[0] : 'gestor'}.</h1>
          {/*
            O briefing do dia (proto:854). O protótipo escreve a data fixa
            "Sexta, 21 de agosto" porque é HTML congelado; aqui a data é a de
            hoje, e cada número sai da mesma consulta que alimenta os cartões
            abaixo — nenhum deles pode discordar do outro.
          */}
          <p data-testid="briefing">
            {dataDeHoje}. {resumo.solicitacoes.vencem_hoje}{' '}
            {resumo.solicitacoes.vencem_hoje === 1 ? 'solicitação vence' : 'solicitações vencem'} hoje,{' '}
            {resumo.aniversariantes.hoje}{' '}
            {resumo.aniversariantes.hoje === 1 ? 'aniversariante' : 'aniversariantes'},{' '}
            {resumo.agenda.hoje}{' '}
            {resumo.agenda.hoje === 1 ? 'compromisso' : 'compromissos'} e{' '}
            {naoLidas} {naoLidas === 1 ? 'notificação não lida' : 'notificações não lidas'}.
          </p>
        </div>

        {/*
          BOTÕES DE AÇÃO DO CABEÇALHO (proto:855).

          Os quatro do protótipo, na mesma ordem, com "Novo contato" em
          destaque como lá.

          Por muito tempo só "Nova ação" esteve aqui, e a tela declarava a
          lacuna: o parâmetro que abre o formulário existia apenas em Ações, e
          um botão "Novo contato" que só abrisse a lista afirmaria o que o
          código não fazia. A causa foi removida — Contatos, Solicitações e
          Visitas passaram a ler `?nova=1`, cada uma consumindo o parâmetro na
          abertura e apagando-o da URL, para que recarregar a página não
          reabra o formulário.

          "Nova visita" aponta para `/visitas`, não para `/agenda`: no
          protótipo o `openDrawer('nova-visita')` não tem drawer
          correspondente, e o único 'nova-visita' de verdade é o botão da tela
          de Visitas, que proto:1318 renomeia para "Agenda interna". `/agenda`
          é a grade semanal, que corresponde a outro drawer do protótipo.
        */}
        <div className="act" data-testid="acoes-cabecalho">
          <button className="btn" onClick={() => navigate('/solicitacoes?nova=1')}>
            Nova solicitação
          </button>
          <button className="btn" onClick={() => navigate('/visitas?nova=1')}>
            Nova visita
          </button>
          <button className="btn" onClick={() => navigate('/acoes?nova=1')}>
            Nova ação
          </button>
          <button className="btn pri" onClick={() => navigate('/contatos?nova=1')}>
            Novo contato
          </button>
        </div>
      </div>

      {/* Alertas críticos em destaque */}
      {criticos.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid var(--crit)', marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 15, color: 'var(--crit)' }}>
              {criticos.length} alerta(s) crítico(s)
            </h2>
            <button className="btn sm" onClick={() => navigate('/alertas')}>
              Ver todos
            </button>
          </div>
          <ul style={{ margin: '8px 0 0 18px', fontSize: 13, color: 'var(--ink2)' }}>
            {criticos.slice(0, 3).map((a) => (
              <li key={a.id}>{a.titulo}</li>
            ))}
          </ul>
        </div>
      )}

      {/*
        OS QUATRO CARTÕES DO CABEÇALHO (proto:856), na mesma ordem: total de
        contatos, aniversariantes de hoje, solicitações e lideranças ativas.
        Ações e Emendas, que antes ocupavam duas destas posições, desceram
        para a faixa de indicadores do rodapé — nenhum número se perdeu.
      */}
      <div className="grid g4">
        <div className="card kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/contatos')}>
          <div className="l">Total de contatos</div>
          <div className="v">{resumo.contatos.total.toLocaleString('pt-BR')}</div>
          <div className="d">
            {resumo.contatos.com_optin.toLocaleString('pt-BR')} com opt-in ·{' '}
            {resumo.contatos.sem_base.toLocaleString('pt-BR')} sem base legal
          </div>
        </div>

        {/*
          ANIVERSARIANTES HOJE.

          O protótipo escreve "3 felicitados pela régua · 4 sem opt-in". A
          primeira metade não é exibível: existe régua com gatilho de
          aniversário e marca de ativa, mas NADA no sistema a dispara — o
          único disparo real acontece na conclusão de solicitação. Dizer
          "felicitados" afirmaria um envio que nunca houve. O que se pode
          afirmar é quem PODE receber e quem exige contato direto.
        */}
        <div
          className="card kpi"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/datas')}
          data-testid="kpi-aniversariantes"
        >
          <div className="l">Aniversariantes hoje</div>
          <div className="v" style={{ color: 'var(--good)' }}>{resumo.aniversariantes.hoje}</div>
          <div className="d">
            {resumo.aniversariantes.com_optin} podem receber a felicitação ·{' '}
            {resumo.aniversariantes.sem_optin} sem opt-in
          </div>
        </div>

        <div
          className="card kpi"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/solicitacoes?recorte=abertas')}
          title="Abre a lista já filtrada pelas solicitações em aberto"
        >
          <div className="l">Solicitações</div>
          <div className="v" style={{ color: resumo.solicitacoes.sla_vencido ? 'var(--crit)' : 'var(--ink)' }}>
            {resumo.solicitacoes.abertas}
          </div>
          {/*
            Os dois recortes são DISJUNTOS: o que já passou da hora é vencido,
            o que ainda vence hoje é "vence hoje". O protótipo soma os dois no
            mesmo `sla <= 0`, o que faz o mesmo pedido aparecer duas vezes.
          */}
          <div className="d">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/solicitacoes?recorte=vencem_hoje');
              }}
              className="chip crit"
              data-testid="chip-vencem-hoje"
            >
              {resumo.solicitacoes.vencem_hoje} vencem hoje
            </a>{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/solicitacoes?recorte=sla_vencido');
              }}
              className="chip warn"
              data-testid="chip-vencidas"
            >
              {resumo.solicitacoes.sla_vencido} vencidas
            </a>
          </div>
        </div>

        {/*
          LIDERANÇAS ATIVAS.

          "Precisam de atenção" é `saude < 50`, e `saude` é uma nota DIGITADA
          pelo gabinete, não um score calculado — a própria entidade diz isso.
          Liderança sem nota não entra na conta: ausência de nota não é nota
          baixa, e contá-la como tal inventaria um problema.
        */}
        <div
          className="card kpi"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/liderancas')}
          data-testid="kpi-liderancas"
        >
          <div className="l">Lideranças ativas</div>
          <div className="v">{resumo.liderancas.ativas}</div>
          <div className="d">
            {resumo.liderancas.precisam_atencao} precisam de atenção ·{' '}
            {resumo.liderancas.visitas_30_dias} visitas em 30 dias
          </div>
        </div>
      </div>

      {/* Mapa de contatos com drill-down (proto:1330-1337) */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0 }}>
            Contatos por território ·{' '}
            <span data-testid="geo-caminho" style={{ fontWeight: 500 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => setGeo({ uf: '', municipio: '' })}
              >
                Brasil
              </button>
              {geo.uf && (
                <>
                  {' › '}
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setGeo({ uf: geo.uf, municipio: '' })}
                  >
                    {geo.uf}
                  </button>
                </>
              )}
              {geo.municipio && <> {' › '}<b>{geo.municipio}</b></>}
            </span>
          </h2>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            clique para descer um nível
            <Info titulo="De onde saem os níveis deste mapa">
              Não há mapa do Brasil nem dos municípios: o sistema só guarda malha geográfica por
              território (<code>terr.bairro.mapa_path</code>). Os níveis saem dos territórios
              cadastrados, não de uma lista fixa de unidades da federação.
            </Info>
          </span>
        </div>

        {!geo.uf && (
          <div className="grid g4" style={{ gap: 10, marginTop: 10 }}>
            {estados.length === 0 ? (
              <div className="empty">Nenhum território cadastrado.</div>
            ) : (
              estados.map((e) => (
                <div
                  key={e.uf}
                  data-testid="geo-nivel"
                  role="button"
                  tabIndex={0}
                  className="card flat kpi"
                  style={{ padding: 12, cursor: 'pointer' }}
                  onClick={() => setGeo({ uf: e.uf, municipio: '' })}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      setGeo({ uf: e.uf, municipio: '' });
                    }
                  }}
                >
                  <div className="l">Estado</div>
                  <div className="v num" style={{ fontSize: 20 }}>
                    {e.uf}
                  </div>
                  <div className="d">
                    <b>{e.contatos.toLocaleString('pt-BR')}</b> contato(s) · {e.territorios}{' '}
                    território(s)
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {geo.uf && !geo.municipio && (
          <div className="grid g4" style={{ gap: 10, marginTop: 10 }}>
            {municipios.length === 0 ? (
              <div className="empty">Sem municípios cadastrados neste estado.</div>
            ) : (
              municipios.map((m) => (
                <div
                  key={m.municipio}
                  data-testid="geo-nivel"
                  role="button"
                  tabIndex={0}
                  className="card flat kpi"
                  style={{ padding: 12, cursor: 'pointer' }}
                  onClick={() => setGeo({ uf: geo.uf, municipio: m.municipio })}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      setGeo({ uf: geo.uf, municipio: m.municipio });
                    }
                  }}
                >
                  <div className="l">Município</div>
                  <div className="v num" style={{ fontSize: 20 }}>
                    {m.municipio}
                  </div>
                  <div className="d">
                    <b>{m.contatos.toLocaleString('pt-BR')}</b> contato(s) · {m.territorios}{' '}
                    território(s)
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {geo.municipio && (
          <>
            {comMalha.length > 0 ? (
              <div className="map" style={{ marginTop: 10 }}>
                <svg viewBox="0 0 640 360" role="img" aria-label={`Mapa de ${geo.municipio}`}>
                  <g stroke="var(--surface)" strokeWidth={2}>
                    {comMalha.map((b) => (
                      <path
                        key={b.id}
                        d={b.path}
                        fill="var(--accent)"
                        // Intensidade = contatos cadastrados, como no protótipo.
                        opacity={0.15 + 0.85 * (b.contatosCadastrados / maxContatos)}
                      >
                        <title>
                          {b.n} · {b.contatosCadastrados} contato(s)
                        </title>
                      </path>
                    ))}
                  </g>
                </svg>
              </div>
            ) : (
              <div className="note" style={{ marginTop: 10 }} data-testid="geo-sem-malha">
                <b>{geo.municipio}</b> ainda não tem malha de bairros cadastrada
                (<code>mapa_path</code>). Os territórios aparecem só como lista abaixo.
              </div>
            )}

            <div className="grid g4" style={{ gap: 10, marginTop: 10 }}>
              {territorios.length === 0 ? (
                <div className="empty">Sem territórios cadastrados neste município.</div>
              ) : (
                territorios.map((b) => (
                  <div
                    key={b.id}
                    data-testid="geo-nivel"
                    role="button"
                    tabIndex={0}
                    className="card flat kpi"
                    style={{ padding: 12, cursor: 'pointer' }}
                    onClick={() => navigate('/territorio')}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        navigate('/territorio');
                      }
                    }}
                  >
                    <div className="l">Território</div>
                    <div className="v num" style={{ fontSize: 20 }}>
                      {b.n}
                    </div>
                    <div className="bar" style={{ margin: '6px 0' }}>
                      <i style={{ width: `${Math.round((b.contatosCadastrados / maxContatos) * 100)}%` }} />
                    </div>
                    <div className="d">
                      <b>{b.contatosCadastrados.toLocaleString('pt-BR')}</b> contato(s) ·
                      necessidade {b.nec}/100
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/*
          O número por território é `contatos_cadastrados`, que o servidor conta
          casando `contato.bairro` com o NOME do território
          (`bairros.service.ts:42`). Quem tem bairro em branco, ou um bairro que
          não corresponde a nenhum território cadastrado, não entra em nível
          nenhum deste mapa — e some sem aviso se a diferença não for dita.
        */}
        {resumo.contatos.total - contatosNoCadastro > 0 && (
          <div className="note warn" style={{ marginTop: 10, fontSize: 12 }} data-testid="geo-lacuna">
            <b>
              {(resumo.contatos.total - contatosNoCadastro).toLocaleString('pt-BR')} contato(s) não
              aparecem neste mapa.
            </b>{' '}
            São os que estão sem bairro preenchido ou com bairro fora dos territórios cadastrados.
            <Info titulo="Por que esses contatos ficam de fora">
              O mapa conta por território, casando o bairro do contato com o NOME do território
              cadastrado. Quem não casa não entra em nível nenhum. Cadastrar o território em
              Territórios faz esses contatos aparecerem.
            </Info>
          </div>
        )}

      </div>

      <div className="grid g2" style={{ marginTop: 12 }}>
        {/* Radar do Dia (proto:852-853) */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Radar do Dia</h2>
            {/*
              O protótipo chama este link de "Como calculamos" e abre a fórmula
              do score. Aqui o rótulo é "Como ordenamos": não há score, e
              "calculamos" prometeria uma conta que o código não faz.
            */}
            <span className="chip neutral" style={{ fontWeight: 500 }}>
              Como ordenamos: pelo prazo
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 10px' }}>
            Prazos já vencidos e o que vence nas próximas 24 horas, do mais atrasado ao mais
            distante.
          </p>

          {radar.length === 0 ? (
            <div className="empty">Nenhum prazo vencido nem compromisso nas próximas 24 horas.</div>
          ) : (
            radar.map((r) => (
              <div
                key={r.chave}
                data-testid="radar-item"
                role="button"
                tabIndex={0}
                title={r.tituloLink}
                onClick={() => navigate(r.destino)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(r.destino);
                  }
                }}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line)',
                  cursor: 'pointer',
                }}
              >
                <span
                  className={`chip ${r.tom}`}
                  style={{ minWidth: 110, justifyContent: 'center', whiteSpace: 'nowrap' }}
                >
                  {r.selo}
                </span>
                <div>
                  <b>{r.titulo}</b>
                  <br />
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{r.detalhe}</span>
                </div>
                <span
                  style={{
                    marginLeft: 'auto',
                    color: 'var(--accent)',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  abrir →
                </span>
              </div>
            ))
          )}

          <div className="note warn" style={{ marginTop: 10, fontSize: 12 }} data-testid="radar-lacuna">
            <b>Sem score composto: 25% dele não tem dado.</b> A ordem é o prazo, em dias e horas
            reais.
            <Info titulo="Por que o score composto do protótipo não é calculado">
              O protótipo ordena este bloco por "urgência 35% + impacto 25% + proximidade temporal
              15% + importância do relacionamento 25%". Não existe, em lugar nenhum do sistema, uma
              medida de <b>importância do relacionamento</b>: o cadastro de contato não guarda
              nenhuma avaliação de vínculo, e a "saúde" da liderança é um número digitado pelo
              gabinete, que só existe para lideranças. Compromissos de agenda e prazos de
              solicitação sequer têm a quem atribuí-lo. Urgência e proximidade também não têm
              escala: no protótipo são constantes fixas no código, não valores medidos.
            </Info>
          </div>
        </div>

        {/* Mapa de Oportunidades (proto:854-856) */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Mapa de Oportunidades</h2>
            <button className="btn sm" onClick={() => navigate('/territorio')}>
              Territórios
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 10px' }}>
            Territórios sem liderança ativa ou sem nenhuma ação concluída, ordenados por
            necessidade.
          </p>

          {oportunidades.length === 0 ? (
            <div className="empty">
              Todo território cadastrado tem liderança ativa e pelo menos uma ação concluída.
            </div>
          ) : (
            oportunidades.map(({ b, motivos }) => (
              <div
                key={b.id}
                data-testid="oportunidade"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/territorio')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/territorio');
                  }
                }}
                style={{ padding: '8px 0', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <b>{b.n}</b>
                  <span className={`chip ${b.nec >= 70 ? 'crit' : 'warn'}`}>
                    necessidade {b.nec}/100
                  </span>
                  <span className="chip neutral">{b.contatosCadastrados} contato(s)</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 3 }}>
                  {motivos.join(' · ')}
                </div>
              </div>
            ))
          )}

          <div className="note" style={{ marginTop: 10, fontSize: 12 }}>
            {/*
              A ausência do "indicador de presença" é declarada aqui pelo mesmo
              motivo do score do radar: os multiplicadores do protótipo
              (proto:832/890) não saem de nenhum dado.
            */}
            Os motivos acima são contagens diretas: zero é zero, não um índice.
            <Info titulo="Por que não há indicador de presença">
              O "indicador de presença" do protótipo é uma soma de pesos arbitrários (visitas × 12
              + lideranças × 14 + contatos ÷ 8 + ações × 6) que não vêm de nenhum dado nem de
              política do gabinete.
            </Info>
          </div>
        </div>
      </div>

      <div className="grid g21" style={{ marginTop: 12 }}>
        {/* Prioridades territoriais */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Territórios por necessidade</h2>
            <button className="btn sm" onClick={() => navigate('/territorio')}>
              Ver território
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 12px' }}>
            Cruzamento do índice de necessidade com o que o gabinete já entregou ali.
          </p>

          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Território</th>
                  <th style={{ textAlign: 'right' }}>Necessidade</th>
                  <th style={{ textAlign: 'right' }}>Solicitações abertas</th>
                  <th style={{ textAlign: 'right' }}>Ações concluídas</th>
                </tr>
              </thead>
              <tbody>
                {resumo.prioridades.map((p) => (
                  <tr key={p.bairro} className="row">
                    <td>
                      <b>{p.bairro}</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span
                        className={`chip ${
                          p.necessidade > 70 ? 'crit' : p.necessidade > 45 ? 'warn' : 'neutral'
                        }`}
                      >
                        {p.necessidade}/100
                      </span>
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {p.suprimido ? (
                        <span
                          style={{ color: 'var(--ink3)' }}
                          title="Poucas pessoas neste território para exibir o número sem identificá-las."
                        >
                          ·
                        </span>
                      ) : (
                        p.solicitacoes_abertas
                      )}
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {p.acoes_concluidas === 0 ? (
                        <span style={{ color: 'var(--crit)' }}>0</span>
                      ) : (
                        p.acoes_concluidas
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Coluna lateral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card kpi">
            <div className="l">Agenda dos próximos 7 dias</div>
            <div className="v" style={{ color: 'var(--accent)' }}>
              {resumo.agenda.proximos_7_dias}
            </div>
            <div className="d">compromissos registrados</div>
            <button className="btn sm" style={{ marginTop: 10 }} onClick={() => navigate('/agenda')}>
              Abrir agenda
            </button>
          </div>

          <div className="card kpi">
            <div className="l">Territórios sem entrega</div>
            <div className="v" style={{ color: resumo.territorios.sem_acao_concluida ? 'var(--warn)' : 'var(--good)' }}>
              {resumo.territorios.sem_acao_concluida}
            </div>
            <div className="d">de {resumo.territorios.total} territórios do mandato</div>
          </div>

          <div className="card kpi">
            <div className="l">Investimento em ações</div>
            <div className="v" style={{ fontSize: 22 }}>
              {brl(resumo.acoes.investimento)}
            </div>
            <div className="d">valor vinculado às ações cadastradas</div>
          </div>

          {/*
            Ações e Emendas ocupavam duas das quatro posições do cabeçalho, que
            o protótipo reserva para aniversariantes e lideranças. Desceram
            para cá em vez de sumir: são indicadores reais, e perder um número
            verdadeiro para ganhar paridade seria trocar informação por forma.
          */}
          <div className="card kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/acoes')}>
            <div className="l">Ações do mandato</div>
            <div className="v">{resumo.acoes.total}</div>
            <div className="d">
              {resumo.acoes.em_andamento} em andamento · {resumo.acoes.concluidas} concluídas
            </div>
          </div>

          <div className="card kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/emendas')}>
            <div className="l">Emendas · execução</div>
            <div className="v" style={{ color: 'var(--good)' }}>{execucao}%</div>
            <div className="d">
              {brl(resumo.emendas.pago)} pagos de {brl(resumo.emendas.indicado)}
            </div>
          </div>

          <div className="card kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/solicitacoes')}>
            <div className="l">Conclusão de solicitações</div>
            <div className="v">{taxaConclusao}%</div>
            <div className="d">
              {resumo.solicitacoes.concluidas} concluídas de {resumo.solicitacoes.total}
            </div>
          </div>
        </div>
      </div>

      {/*
        HOJE NA AGENDA e CADASTROS RECENTES (proto:864-866).

        O protótipo tem um terceiro cartão nesta faixa, "Comunicação · 7 dias",
        com números literais no HTML (2.318 enviadas, 96,7% entregues). Ele
        não está aqui porque a taxa de entrega depende de um retorno de status
        do WhatsApp que o sistema não registra para todas as mensagens — o
        número existiria, mas a porcentagem seria inventada.
      */}
      <div className="grid g3" style={{ marginTop: 12 }}>
        {/*
          SOLICITAÇÕES POR TERRITÓRIO · 30 DIAS (proto:864).

          Sujeito à mesma supressão por k-anonimato que já governa as
          prioridades territoriais: contagem de pedidos por território é
          contagem de quem pediu, e um território com uma ou duas solicitações
          identifica pessoas. Suprimido aparece como "—", nunca como zero:
          zero diria "ninguém pediu nada aqui", que é o oposto do que houve.
        */}
        <div className="card" data-testid="solicitacoes-por-territorio">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Solicitações por território · 30d</h2>
            <button className="btn sm" onClick={() => navigate('/solicitacoes')}>
              Ver
            </button>
          </div>
          {resumo.solicitacoes_por_territorio.length === 0 ? (
            <div className="empty" style={{ marginTop: 10 }}>
              Nenhuma solicitação nos últimos 30 dias.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              {(() => {
                const maior = Math.max(
                  1,
                  ...resumo.solicitacoes_por_territorio.map((t) => t.total ?? 0),
                );
                return resumo.solicitacoes_por_territorio.map((t) => (
                  <div
                    key={t.territorio}
                    style={{ padding: '6px 0', cursor: t.suprimido ? 'default' : 'pointer' }}
                    onClick={() =>
                      !t.suprimido && navigate(`/solicitacoes?bairro=${encodeURIComponent(t.territorio)}`)
                    }
                  >
                    <div style={{ display: 'flex', fontSize: 13, gap: 8 }}>
                      <span>{t.territorio}</span>
                      <span style={{ marginLeft: 'auto', color: 'var(--ink3)' }}>
                        {t.suprimido ? '—' : t.total}
                      </span>
                    </div>
                    <div className="bar" style={{ marginTop: 4 }}>
                      <i style={{ width: `${Math.round(((t.total ?? 0) / maior) * 100)}%` }} />
                    </div>
                  </div>
                ));
              })()}
              {resumo.solicitacoes_por_territorio.some((t) => t.suprimido) && (
                <p
                  style={{ fontSize: 12, color: 'var(--ink3)', margin: '8px 0 0' }}
                  data-testid="aviso-supressao-territorio"
                >
                  "—" é contagem suprimida. Não é zero.
                  <Info titulo="Por que a contagem foi suprimida">
                    O território tem poucos pedidos, e exibir o número identificaria quem pediu.
                    Não é zero: zero diria que ninguém pediu nada ali.
                  </Info>
                </p>
              )}
            </div>
          )}
        </div>

        <div className="card" data-testid="hoje-na-agenda">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Hoje na agenda</h2>
            <button className="btn sm" onClick={() => navigate('/agenda')}>
              Agenda
            </button>
          </div>
          {resumo.agenda.compromissos_hoje.length === 0 ? (
            <div className="empty" style={{ marginTop: 10 }}>
              Nenhum compromisso hoje.
            </div>
          ) : (
            <div className="tl" style={{ marginTop: 10 }}>
              {resumo.agenda.compromissos_hoje.map((c) => (
                <div
                  key={c.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/agenda?evento=${c.id}`)}
                >
                  {c.hora} · {c.titulo}
                  {/* Sem local cadastrado a linha fica sem o sufixo, em vez de dizer "—". */}
                  {c.local && <small>{c.local}</small>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" data-testid="cadastros-recentes">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Cadastros recentes</h2>
            <button className="btn sm" onClick={() => navigate('/contatos')}>
              Contatos
            </button>
          </div>
          {resumo.cadastros_recentes.length === 0 ? (
            <div className="empty" style={{ marginTop: 10 }}>
              Nenhum contato cadastrado ainda.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              {resumo.cadastros_recentes.map((c) => (
                <div
                  key={c.id}
                  className="n"
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '6px 0',
                    borderBottom: '1px solid var(--line)',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/contatos?contato=${c.id}`)}
                >
                  <span>{c.nome}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--ink3)' }}>
                    {c.territorio ?? 'sem território'}
                  </span>
                  <span className={`chip ${c.base_legal === 'optin' ? 'good' : 'warn'}`}>
                    {c.base_legal === 'optin' ? 'opt-in' : 'sem base'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
